import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarCheck, CheckCircle2, Loader2, Phone, XCircle } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric",
        });
    } catch {
        return iso;
    }
};

const Book = () => {
    const [params] = useSearchParams();
    const jobId = params.get("j");
    const token = params.get("t");
    const [state, setState] = useState({ status: "loading" });
    const [submitting, setSubmitting] = useState(false);

    // Fetch booking details on load — NO side effects
    useEffect(() => {
        if (!jobId || !token) {
            setState({ status: "error", message: "This booking link is missing information." });
            return;
        }
        axios
            .get(`${API}/book/${jobId}?t=${encodeURIComponent(token)}`)
            .then((r) => setState({ status: "preview", details: r.data }))
            .catch((err) =>
                setState({
                    status: "error",
                    message:
                        err?.response?.data?.detail ||
                        "We couldn't verify this booking link. It may have expired.",
                }),
            );
    }, [jobId, token]);

    const confirm = async () => {
        setSubmitting(true);
        try {
            const r = await axios.post(`${API}/book/${jobId}?t=${encodeURIComponent(token)}`);
            setState({ status: "done", details: r.data });
        } catch (err) {
            setState({
                status: "error",
                message: err?.response?.data?.detail || "Something went wrong sending your request.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center px-4 py-8"
            style={{
                background:
                    "linear-gradient(rgba(10,10,10,0.85), rgba(10,10,10,0.95)), url('https://images.pexels.com/photos/3721272/pexels-photo-3721272.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400') center/cover",
            }}
            data-testid="book-page"
        >
            <div className="dh-card w-full max-w-lg p-8 bg-white">
                <div className="flex items-center gap-4 mb-6">
                    <div className="h-16 w-16 flex items-center justify-center border-2 border-[color:var(--dh-line)] bg-[color:var(--dh-ink)] p-1">
                        <img
                            src="/dragon-heat-logo.png"
                            alt="Dragon Heat"
                            className="max-h-full max-w-full object-contain"
                        />
                    </div>
                    <div>
                        <p className="text-label">Dragon Heat</p>
                        <h1 className="font-display font-black text-2xl uppercase tracking-tight leading-none">
                            {state.status === "done" ? "Request sent" : "Book your service"}
                        </h1>
                    </div>
                </div>

                {state.status === "loading" && (
                    <div className="flex items-center gap-3 text-neutral-600" data-testid="book-loading">
                        <Loader2 className="animate-spin" size={18} />
                        <p>Checking your booking link…</p>
                    </div>
                )}

                {state.status === "preview" && (
                    <div data-testid="book-preview">
                        <p className="text-base mb-4">
                            Hi {state.details.customer_name || "there"} — just to confirm the details before
                            we send this to Hayden:
                        </p>

                        <div className="border-2 border-[color:var(--dh-line)] bg-[color:var(--dh-paper-2)] p-4 space-y-3">
                            <div>
                                <p className="text-label">Service</p>
                                <p className="font-display font-black uppercase text-lg leading-tight">
                                    {state.details.service_name}
                                </p>
                            </div>
                            <div>
                                <p className="text-label">Address</p>
                                <p className="text-base">{state.details.address}</p>
                            </div>
                            {state.details.next_due_date && (
                                <div>
                                    <p className="text-label">Currently due</p>
                                    <p className="text-base">{fmtDate(state.details.next_due_date)}</p>
                                </div>
                            )}
                        </div>

                        {state.details.already_requested && (
                            <p
                                className="mt-4 text-sm text-[color:var(--dh-amber)] font-mono"
                                data-testid="book-already"
                            >
                                Heads-up: a request has already been sent for this booking. Confirming again
                                will re-send it to Hayden.
                            </p>
                        )}

                        <button
                            onClick={confirm}
                            disabled={submitting}
                            data-testid="book-confirm-btn"
                            className="dh-btn w-full mt-6 text-lg"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="animate-spin" size={18} /> Sending…
                                </>
                            ) : (
                                <>
                                    <CalendarCheck size={20} /> Yes — book my service
                                </>
                            )}
                        </button>

                        <p className="text-xs text-neutral-500 mt-4 font-mono text-center">
                            Hayden will call you to lock in a date.
                        </p>
                    </div>
                )}

                {state.status === "done" && (
                    <div data-testid="book-success">
                        <div className="flex items-center gap-3 mb-4">
                            <CheckCircle2 className="text-[color:var(--dh-green)]" size={32} />
                            <h2 className="font-display font-black text-2xl uppercase leading-none">
                                Request received
                            </h2>
                        </div>
                        <p className="text-base">
                            Thanks{state.details.customer_name ? `, ${state.details.customer_name}` : ""}.
                            We've let Dragon Heat know you'd like to book your service at{" "}
                            <strong>{state.details.address}</strong>. Hayden will be in touch shortly to lock
                            in a date and time.
                        </p>
                        <div className="mt-6 border-t-2 border-neutral-200 pt-4">
                            <p className="text-label mb-2">Need it sorted sooner?</p>
                            <a
                                href="tel:07783861244"
                                className="dh-btn inline-flex"
                                data-testid="book-call-btn"
                            >
                                <Phone size={16} /> Call 07783 861 244
                            </a>
                        </div>
                    </div>
                )}

                {state.status === "error" && (
                    <div data-testid="book-error">
                        <div className="flex items-center gap-3 mb-4">
                            <XCircle className="text-[color:var(--dh-red)]" size={32} />
                            <h2 className="font-display font-black text-2xl uppercase leading-none">
                                Something's up
                            </h2>
                        </div>
                        <p className="text-base">{state.message}</p>
                        <p className="text-sm text-neutral-600 mt-3">
                            No worries — just email{" "}
                            <a
                                href="mailto:info@dragon-heat.co.uk"
                                className="text-[color:var(--dh-orange)] underline font-mono"
                            >
                                info@dragon-heat.co.uk
                            </a>{" "}
                            or call{" "}
                            <a href="tel:07783861244" className="font-mono underline">
                                07783 861 244
                            </a>{" "}
                            and we'll book you in.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Book;
